/**
 * Client HTTP generique pour communiquer avec l'API backend.
 * Gere automatiquement les tokens Bearer, le refresh, et les erreurs 401.
 *
 * Independant de tout module metier - configure via httpConfig.
 */

import { getHttpConfig } from '../../config/httpConfig';
import { tokenManager } from '../security/TokenManager';
import type { ApiResponse } from '../../types/api.types';

/**
 * Options pour une requete HTTP
 */
export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  requiresAuth?: boolean;
  skipRefresh?: boolean;
}

/**
 * Erreur API personnalisee ..
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Client HTTP avec gestion automatique des tokens
 */
export class ApiClient {
  /**
   * Verrou single-flight : evite que plusieurs requetes 401 quasi-simultanees
   * (plusieurs composants qui chargent leurs donnees au montage) declenchent
   * chacune leur propre appel /api/auth/refresh. Le refresh token etant a
   * rotation (chaque usage invalide la valeur precedente), un 2e appel
   * concurrent utilisant l'ancien cookie recevrait un 401 et deconnecterait
   * a tort un utilisateur pourtant valide. Tous les appelants concurrents
   * attendent donc la meme promesse de refresh en cours.
   */
  private refreshInFlight: Promise<{ accessToken: string }> | null = null;

  private buildUrl(endpoint: string): string {
    const config = getHttpConfig();
    const baseUrl = config.apiBaseUrl;

    if (endpoint.startsWith('http')) return endpoint;
    if (!baseUrl) return endpoint;

    const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${cleanBase}${cleanEndpoint}`;
  }

  private prepareHeaders(options: RequestOptions): Headers {
    const headers = new Headers({
      'Content-Type': 'application/json',
      ...options.headers,
    });

    if (options.requiresAuth !== false) {
      const authHeader = tokenManager.getAuthorizationHeader();
      if (authHeader) {
        headers.set('Authorization', authHeader);
      }
    }

    return headers;
  }

  async request<T = unknown>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const config = getHttpConfig();

    // Pre-refresh proactif : si le token est proche de l'expiration, tenter un refresh silencieux
    if (
      options.requiresAuth !== false &&
      !options.skipRefresh &&
      config.tokens.autoRefresh
    ) {
      const accessToken = tokenManager.getAccessToken();
      if (accessToken && tokenManager.needsRefresh(accessToken, config.tokens.refreshMargin / 1000)) {
        try {
          await this.refreshAccessToken();
        } catch {
          // Continuer avec l'ancien token
        }
      }
    }

    const url = this.buildUrl(endpoint);
    const method = options.method || 'GET';
    const headers = this.prepareHeaders(options);

    // credentials: 'include' sur tout — nécessaire pour que le cookie httpOnly du refresh
    // token parte/soit accepté sur connexion/refresh/logout (frontend et backend sur des
    // origines différentes en dev). Sans incidence sur les routes qui n'ont pas de cookie.
    const requestInit: RequestInit = { method, headers, credentials: 'include' };

    if (options.body && method !== 'GET') {
      requestInit.body = JSON.stringify(options.body);
    }

    try {
      const response = await fetch(url, requestInit);

      let data: ApiResponse<T>;
      try {
        data = await response.json();
      } catch {
        throw new ApiError(
          'Réponse invalide du serveur',
          response.status,
          'INVALID_RESPONSE'
        );
      }

      if (!response.ok) {
        // 401 : tenter un refresh + retry avant de declencher onUnauthorized
        if (response.status === 401 && options.requiresAuth !== false && !options.skipRefresh) {
          try {
            await this.refreshAccessToken();
            return await this.request<T>(endpoint, { ...options, skipRefresh: true });
          } catch {
            console.warn('[ApiClient] 401 + refresh echoue - Deconnexion', { code: data.code, message: data.message });
            config.auth.onUnauthorized();
            return data;
          }
        }

        // 429 : rate limit atteint
        if (response.status === 429) {
          console.warn('[ApiClient] 429 Rate limit atteint', { code: data.code, message: data.message });
          throw new ApiError(
            data.message || 'Trop de requêtes. Veuillez patienter avant de réessayer.',
            429,
            data.code || 'TROP_DE_REQUETES',
            data.data
          );
        }

        throw new ApiError(
          data.message || 'Une erreur est survenue',
          response.status,
          data.code,
          data.data
        );
      }

      return data;
    } catch (error) {
      if (error instanceof ApiError) throw error;

      throw new ApiError(
        'Erreur de connexion au serveur',
        0,
        'NETWORK_ERROR'
      );
    }
  }

  async get<T = unknown>(
    endpoint: string,
    options: Omit<RequestOptions, 'method' | 'body'> = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T = unknown>(
    endpoint: string,
    body?: unknown,
    options: Omit<RequestOptions, 'method' | 'body'> = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  async put<T = unknown>(
    endpoint: string,
    body?: unknown,
    options: Omit<RequestOptions, 'method' | 'body'> = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  async patch<T = unknown>(
    endpoint: string,
    body?: unknown,
    options: Omit<RequestOptions, 'method' | 'body'> = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body });
  }

  async delete<T = unknown>(
    endpoint: string,
    body?: unknown,
    options: Omit<RequestOptions, 'method' | 'body'> = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE', body });
  }

  /**
   * Rafraîchit l'access token via le cookie httpOnly du refresh token — le JS ne le voit
   * jamais, `credentials: 'include'` (déjà systématique sur `request()`) suffit à ce qu'il
   * parte automatiquement.
   */
  async refreshAccessToken(): Promise<{ accessToken: string }> {
    if (this.refreshInFlight) return this.refreshInFlight;

    this.refreshInFlight = this.doRefreshAccessToken().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async doRefreshAccessToken(): Promise<{ accessToken: string }> {
    const config = getHttpConfig();

    try {
      const response = await this.post<{ tokens: { accessToken: string } }>(
        config.auth.refreshEndpoint,
        undefined,
        { requiresAuth: false, skipRefresh: true }
      );

      if (!response.succes || !response.data?.tokens) {
        throw new ApiError('Échec du refresh token', 401, 'REFRESH_FAILED');
      }

      const { accessToken } = response.data.tokens;
      tokenManager.setAccessToken(accessToken);
      return { accessToken };
    } catch (error) {
      tokenManager.clearTokens();
      throw error;
    }
  }

}

/** Instance singleton */
export const apiClient = new ApiClient();

/** Helper pour extraire le message d'erreur */
export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Une erreur inconnue est survenue';
}

/** Helper pour verifier si c'est une ApiError */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
